import { createContext, useContext, useState, useCallback } from 'react';
import type { SelectionArea } from './useSelection';

export interface SelectionEntry {
  area: SelectionArea;
  itemId: string;
  dataId?: string; // underlying DataItem id for deletion (optional for area like folder-node)
}

export interface GlobalSelectionContextValue {
  entries: SelectionEntry[];
  addEntries: (entries: SelectionEntry[]) => void;
  removeEntries: (itemIds: string[]) => void;
  clearAll: () => void;
  clearArea: (area: SelectionArea) => void;
  getEntriesByArea: (area: SelectionArea) => SelectionEntry[];
  totalCount: number;
}

export const GlobalSelectionContext = createContext<GlobalSelectionContextValue>({
  entries: [],
  addEntries: () => {},
  removeEntries: () => {},
  clearAll: () => {},
  clearArea: () => {},
  getEntriesByArea: () => [],
  totalCount: 0,
});

export function useGlobalSelectionProvider(): GlobalSelectionContextValue {
  const [entries, setEntries] = useState<SelectionEntry[]>([]);

  const addEntries = useCallback((newEntries: SelectionEntry[]) => {
    setEntries(prev => {
      const ids = new Set(prev.map(e => e.itemId));
      const toAdd = newEntries.filter(e => !ids.has(e.itemId));
      return [...prev, ...toAdd];
    });
  }, []);

  const removeEntries = useCallback((itemIds: string[]) => {
    const removeSet = new Set(itemIds);
    setEntries(prev => prev.filter(e => !removeSet.has(e.itemId)));
  }, []);

  const clearAll = useCallback(() => setEntries([]), []);

  const clearArea = useCallback((area: SelectionArea) => {
    setEntries(prev => prev.filter(e => e.area !== area));
  }, []);

  const getEntriesByArea = useCallback((area: SelectionArea) => {
    return entries.filter(e => e.area === area);
  }, [entries]);

  return {
    entries,
    addEntries,
    removeEntries,
    clearAll,
    clearArea,
    getEntriesByArea,
    totalCount: entries.length,
  };
}

export function useGlobalSelection() {
  return useContext(GlobalSelectionContext);
}
